import {
  createSignal,
  JSX,
  onCleanup,
  onMount,
  ParentComponent,
  Show,
} from "solid-js";
import { Portal } from "solid-js/web";

export interface ITooltipProps {
  enabled?: boolean;
  message: string | JSX.Element;
  tooltipClass?: string;
  containerClass?: string;
  onHoverClass?: boolean;
  hideArrow?: boolean;
  hideDelayMs?: number;
}

const Tooltip: ParentComponent<ITooltipProps> = (props) => {
  let containerDiv!: HTMLDivElement;
  let tooltipElm!: HTMLSpanElement;

  const showArrow = () => props.hideArrow !== true;

  const [mouseOnTooltip, setMouseOnTooltip] = createSignal(false);
  const [closeTimeout, setCloseTimeout] = createSignal<number | null>(null);

  const onMouseEnter = () => {
    if (props.enabled === false) {
      return;
    }
    // this prevents screen jank as the menu position is calculated
    tooltipElm.style.display = "block";

    tooltipElm.style.top = `${
      containerDiv.getBoundingClientRect().top - (tooltipElm.clientHeight + 5)
    }px`;

    tooltipElm.style.left = `${Math.min(
      containerDiv.getBoundingClientRect().left,
      window.innerWidth - 15 - tooltipElm.clientWidth,
    )}px`;
    tooltipElm.style.position = "absolute";
  };

  const onMouseLeave = () => {
    if (props.enabled === false) {
      return;
    }
    if (props.hideDelayMs) {
      const existingTimeout = closeTimeout();
      if (existingTimeout) {
        window.clearTimeout(existingTimeout);
      }
      setCloseTimeout(
        window.setTimeout(() => {
          if (mouseOnTooltip()) {
            return;
          }
          tooltipElm.style.display = "none";
        }, props.hideDelayMs),
      );
    } else {
      tooltipElm.style.display = "none";
    }
  };

  // this prevents the tooltip from detaching from it's parent when scrolling
  const onScrollStart = () => {
    if (props.enabled === false) {
      return;
    }
    tooltipElm.style.display = "none";
  };

  onMount(() => {
    document
      .querySelector("#mainContainer")
      ?.addEventListener("scroll", onScrollStart);
  });

  onCleanup(() => {
    document
      .querySelector("#mainContainer")
      ?.removeEventListener("scroll", onScrollStart);
  });

  return (
    <div
      ref={containerDiv!}
      class={`${props.containerClass} group relative`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Show when={props.enabled || props.enabled === undefined}>
        <Portal mount={document.body}>
          <span
            ref={tooltipElm!}
            onMouseEnter={() => setMouseOnTooltip(true)}
            onMouseLeave={() => {
              setMouseOnTooltip(false);
              onMouseLeave();
            }}
            class={`${props.tooltipClass} absolute left-0 top-0 z-50 hidden max-w-[300px] rounded-md border border-primary-700 bg-primary-700 px-4 py-2 text-xs text-white opacity-90 shadow`}
          >
            {props.message}
            <Show when={showArrow()}>
              <div class="absolute left-4 top-full size-0 -translate-x-1/2 border-x-8 border-t-8 border-transparent border-t-primary-700" />
            </Show>
          </span>
        </Portal>
      </Show>
      {props.children}
    </div>
  );
};

export default Tooltip;
